import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    MenuItem,
    Box,
    Typography,
    Grid,
    Alert,
    IconButton
} from '@mui/material';
import { Close as CloseIcon, Add as AddIcon, Check as CheckIcon } from '@mui/icons-material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { frFR } from '@mui/x-date-pickers/locales';
import dayjs from 'dayjs';
import { COLORS } from '../../../settings/themeSettings';

const DashboardCriteriaModal = ({
    open,
    onClose,
    onConfirmCriteria,
    onOpenCombined,
    initialCriteria = {}
}) => {
    const [startDate, setStartDate] = useState(initialCriteria.startDate ? dayjs(initialCriteria.startDate) : dayjs().startOf('year'));
    const [endDate, setEndDate] = useState(initialCriteria.endDate ? dayjs(initialCriteria.endDate) : dayjs().endOf('year'));
    const [echelonnement, setEchelonnement] = useState(initialCriteria.echelonnement || '');
    const [typeActivite, setTypeActivite] = useState(initialCriteria.typeActivite || 'Tous');
    const [errorMessage, setErrorMessage] = useState('');

    const handleOk = () => {
        if (!echelonnement || echelonnement.trim() === '') {
            setErrorMessage('Le champ "Échelonnement" est obligatoire et doit être sélectionné avant validation.');
            return;
        }
        setErrorMessage('');
        onConfirmCriteria({
            startDate: startDate ? startDate.format('YYYY-MM-DD') : '',
            endDate: endDate ? endDate.format('YYYY-MM-DD') : '',
            echelonnement,
            typeActivite
        });
    };

    const handlePlus = () => {
        onOpenCombined({
            startDate: startDate ? startDate.format('YYYY-MM-DD') : '',
            endDate: endDate ? endDate.format('YYYY-MM-DD') : '',
            echelonnement: echelonnement || 'Mois',
            typeActivite
        });
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs} localeText={frFR.components.MuiLocalizationProvider.defaultProps.localeText}>
            <Dialog 
                open={open} 
                onClose={onClose} 
                maxWidth="sm" 
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 2,
                        boxShadow: '0 8px 32px rgba(0,0,0,0.15)'
                    }
                }}
            >
                {/* En-tête officiel de la modale */}
                <DialogTitle sx={{ 
                    bgcolor: COLORS.primary || '#0ea5e9', 
                    color: 'white', 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    py: 1.5
                }}>
                    <Box>
                        <Typography variant="h6" fontWeight="bold" component="div">
                            Tableau de bord personnalisé
                        </Typography>
                        <Typography variant="caption" sx={{ opacity: 0.9 }}>
                            Sélection des critères de consultation et d'édition
                        </Typography>
                    </Box>
                    <IconButton onClick={onClose} size="small" sx={{ color: 'white' }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={{ pt: 3, pb: 2 }}>
                    {errorMessage && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {errorMessage}
                        </Alert>
                    )}

                    <Box sx={{ mt: 1 }}>
                        <Grid container spacing={2}>
                            {/* Période de / à */}
                            <Grid item xs={12}>
                                <Typography variant="subtitle2" color="text.secondary" fontWeight="600" sx={{ mb: 1 }}>
                                    Période d'analyse
                                </Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <DatePicker
                                    label="De"
                                    value={startDate}
                                    onChange={(newVal) => setStartDate(newVal)}
                                    slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <DatePicker
                                    label="À"
                                    value={endDate}
                                    onChange={(newVal) => setEndDate(newVal)}
                                    slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                                />
                            </Grid>

                            {/* Échelonnement obligatoire */}
                            <Grid item xs={12} sx={{ mt: 1 }}>
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    label="Échelonnement (Obligatoire)"
                                    value={echelonnement}
                                    onChange={(e) => {
                                        setEchelonnement(e.target.value);
                                        if (e.target.value) setErrorMessage('');
                                    }}
                                    error={Boolean(errorMessage && !echelonnement)}
                                    helperText={
                                        errorMessage && !echelonnement 
                                            ? 'Veuillez choisir un mode de découpage' 
                                            : 'Détermine la segmentation des 4 périodes comparatives'
                                    }
                                >
                                    <MenuItem value="" disabled>
                                        <em>-- Sélectionner un échelonnement --</em>
                                    </MenuItem>
                                    <MenuItem value="Date">Date</MenuItem>
                                    <MenuItem value="Mois">Mois</MenuItem>
                                    <MenuItem value="Trimestre">Trimestre</MenuItem>
                                    <MenuItem value="Semestre">Semestre</MenuItem>
                                </TextField>
                            </Grid>

                            {/* Type d'activité */}
                            <Grid item xs={12}>
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    label="Type d'activité"
                                    value={typeActivite}
                                    onChange={(e) => setTypeActivite(e.target.value)}
                                >
                                    <MenuItem value="Tous">Tous (par défaut)</MenuItem>
                                    <MenuItem value="Billetterie">Billetterie</MenuItem>
                                    <MenuItem value="Tourisme">Tourisme</MenuItem>
                                </TextField>
                            </Grid>
                        </Grid>
                    </Box>
                </DialogContent>

                {/* Boutons d'actions : Plus, OK, Annuler */}
                <DialogActions sx={{ px: 3, pb: 2.5, justifyContent: 'space-between' }}>
                    <Button 
                        variant="outlined" 
                        color="secondary" 
                        startIcon={<AddIcon />}
                        onClick={handlePlus}
                        sx={{ textTransform: 'none', fontWeight: 'bold' }}
                    >
                        Plus (Mode Combiné)
                    </Button>
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                        <Button 
                            variant="outlined" 
                            color="inherit" 
                            onClick={onClose}
                            sx={{ textTransform: 'none' }}
                        >
                            Annuler
                        </Button>
                        <Button 
                            variant="contained" 
                            color="primary" 
                            startIcon={<CheckIcon />}
                            onClick={handleOk}
                            sx={{ textTransform: 'none', fontWeight: 'bold', px: 3 }}
                        >
                            OK
                        </Button>
                    </Box>
                </DialogActions>
            </Dialog>
        </LocalizationProvider>
    );
};

export default DashboardCriteriaModal;
